import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import Icon, { IconName } from '../components/Icon';
import DemoPreview from '../components/DemoPreview';
import LanguagePicker from '../components/LanguagePicker';
import { useI18n } from '../i18n';
interface Props { navigate: (s: Screen, state?: AppState) => void; }

// Icons stay in code (visual, language-independent); the feature copy comes from
// the translation catalog and is zipped with these in order.
const FEATURE_ICONS: IconName[] = ['mic', 'brain', 'check-circle', 'clipboard', 'heart', 'lock'];

export default function Landing({ navigate }: Props) {
  const { t, tm } = useI18n();
  const [openFaq, setOpenFaq] = useState<number|null>(null);
  const problems = tm<{ t: string; d: string }[]>('landing.problems');
  const features = tm<{ title: string; desc: string }[]>('landing.features');
  const faqs = tm<{ q: string; a: string }[]>('landing.faqs');
  return (
    <div style={{paddingBottom:0}}>
      {/* Header */}
      <div style={{background:'white',boxShadow:'var(--shadow-sm)'}}>
        <div className="mkt-inner" style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 24px'}}>
          <div style={{display:'flex',alignItems:'center'}}>
            <img src='/logo.png' alt='Decode My Student' style={{height:44,width:44,borderRadius:9,marginRight:10}}/><span style={{fontFamily:'Playfair Display',fontSize:19,fontWeight:700,color:'var(--teal)'}}>Decode My Student</span>
          </div>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <LanguagePicker tone="dark" />
            <button onClick={()=>navigate('demo')} style={{background:'none',color:'var(--teal)',fontWeight:600,fontSize:14,padding:'8px 12px'}}>{t('common.seeDemo')}</button>
            <button onClick={()=>navigate('schools')} style={{background:'none',color:'var(--teal)',fontWeight:600,fontSize:14,padding:'8px 12px'}}>{t('common.getSchoolLicense')}</button>
            <button onClick={()=>navigate('login')} style={{background:'none',color:'var(--teal)',fontWeight:600,fontSize:14,padding:'8px 12px'}}>{t('common.logIn')}</button>
            <button onClick={()=>navigate('signup')} className="btn-primary" style={{width:'auto',padding:'8px 18px',fontSize:14}}>{t('common.startFree')}</button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="landing-hero">
        <div className="mkt-hero-inner" style={{position:'relative',zIndex:1,textAlign:'center'}}>
          <div className="hero-badge">{t('landing.heroBadge')}</div>
          <h1 className="mkt-h1" style={{color:'white',marginBottom:16}}>{t('landing.heroTitle')}</h1>
          <p style={{fontSize:16,color:'rgba(255,255,255,0.85)',marginBottom:28,lineHeight:1.6}}>{t('landing.heroSubtitle')}</p>
          <div style={{maxWidth:400,margin:'0 auto'}}>
            <p style={{color:'rgba(255,255,255,0.9)',fontSize:14,fontWeight:600,marginBottom:10}}>{t('landing.pathPrompt')}</p>
            <div style={{display:'flex',gap:10,marginBottom:10}}>
              <button onClick={()=>navigate('signup',{accountType:'teacher'})} style={{flex:1,background:'var(--amber)',color:'white',padding:'15px 12px',borderRadius:12,fontSize:15,fontWeight:700,border:'none',cursor:'pointer'}}>{t('landing.teacherPath')}</button>
              <button onClick={()=>navigate('signup',{accountType:'parent'})} style={{flex:1,background:'white',color:'var(--teal)',padding:'15px 12px',borderRadius:12,fontSize:15,fontWeight:700,border:'none',cursor:'pointer'}}>{t('landing.parentPath')}</button>
            </div>
            <button onClick={()=>navigate('demo')} style={{background:'rgba(255,255,255,0.15)',color:'white',padding:'12px 24px',borderRadius:10,fontSize:15,fontWeight:600,width:'100%',marginBottom:10,border:'1px solid rgba(255,255,255,0.4)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}><Icon name="play" size={16}/> {t('landing.seeDemo')}</button>
            <button onClick={()=>navigate('signup',{intent:'subscribe'})} style={{background:'rgba(255,255,255,0.15)',color:'white',padding:'12px 24px',borderRadius:10,fontSize:15,fontWeight:600,width:'100%',marginBottom:10,border:'1px solid rgba(255,255,255,0.4)',cursor:'pointer'}}>{t('landing.skipTrial')}</button>
            <p style={{color:'rgba(255,255,255,0.7)',fontSize:13}}>{t('landing.noCard')}</p>
          </div>
        </div>
      </div>

      {/* Credibility strip */}
      <div style={{background:'var(--teal)',color:'white',padding:'28px 24px'}}>
        <div style={{maxWidth:760,margin:'0 auto',textAlign:'center'}}>
          <p style={{fontSize:15,fontStyle:'italic',lineHeight:1.7,marginBottom:16,opacity:0.95}}>{t('landing.credibilityQuote')}</p>
          <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center'}}>
            {[t('landing.badgeNeuro'),t('landing.badgeTrauma')].map(f=><span key={f} style={{background:'rgba(255,255,255,0.15)',color:'white',padding:'4px 12px',borderRadius:20,fontSize:12,fontWeight:600}}>{f}</span>)}
          </div>
        </div>
      </div>

      {/* Problems */}
      <div style={{padding:'48px 0'}}>
        <div className="mkt-inner">
          <h2 style={{textAlign:'center',fontSize:26,marginBottom:24}}>{t('landing.problemsTitle')}</h2>
          <div className="mkt-problems">
            {problems.map((p,i)=>(
              <div key={i} className="card" style={{display:'flex',gap:14,alignItems:'flex-start'}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:'var(--coral)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:16,flexShrink:0}}>{i+1}</div>
                <div><p style={{fontWeight:600,marginBottom:4}}>{p.t}</p><p style={{fontSize:14,color:'var(--text-secondary)'}}>{p.d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div style={{padding:'0 0 48px'}}>
        <div className="mkt-inner">
          <p style={{textAlign:'center',fontSize:20,fontFamily:'Playfair Display',color:'var(--teal)',fontStyle:'italic',marginBottom:24}}>{t('landing.builtToHelp')}</p>
          <div className="mkt-features">{features.map((f,i)=><div key={i} className="card" style={{textAlign:'center'}}><div style={{display:'flex',justifyContent:'center',marginBottom:10,color:'var(--teal)'}}><Icon name={FEATURE_ICONS[i]} size={30} strokeWidth={1.6}/></div><p style={{fontWeight:600,fontSize:14,marginBottom:4}}>{f.title}</p><p style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.4}}>{f.desc}</p></div>)}</div>
        </div>
      </div>

      {/* See what you'll get — clearly-labeled example */}
      <DemoPreview/>

      {/* Pricing */}
      <div style={{background:'var(--teal-light)',padding:'48px 0'}}>
        <div className="mkt-inner">
          <h2 style={{textAlign:'center',marginBottom:28,fontSize:26}}>{t('landing.pricingTitle')}</h2>
          <div className="mkt-pricing">
            <div className="card" style={{border:'2px solid var(--teal)',position:'relative',paddingTop:28}}>
              <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background:'var(--amber)',color:'white',padding:'3px 16px',borderRadius:20,fontSize:12,fontWeight:700,whiteSpace:'nowrap'}}>{t('landing.bestValue')}</div>
              <p style={{textAlign:'center',fontWeight:700,fontSize:13,color:'var(--teal)',marginBottom:4}}>{t('landing.annualPlan')}</p>
              <p style={{textAlign:'center',fontSize:30,fontWeight:700}}>{t('landing.annualPrice')}<span style={{fontSize:14,fontWeight:400}}>{t('landing.perMonth')}</span></p>
              <p style={{textAlign:'center',fontSize:13,color:'var(--text-secondary)',marginBottom:16}}>{t('landing.annualBilled')}</p>
              <button onClick={()=>navigate('signup')} className="btn-primary">{t('landing.startFreeTrial')}</button>
              <button onClick={()=>navigate('signup',{intent:'subscribe'})} style={{background:'none',border:'none',color:'var(--teal)',fontWeight:600,fontSize:13,marginTop:10,width:'100%',cursor:'pointer'}}>{t('landing.subscribeAnnually')}</button>
            </div>
            <div className="card">
              <p style={{textAlign:'center',fontWeight:700,fontSize:13,color:'var(--text-secondary)',marginBottom:4}}>{t('landing.monthlyPlan')}</p>
              <p style={{textAlign:'center',fontSize:30,fontWeight:700}}>{t('landing.monthlyPrice')}<span style={{fontSize:14,fontWeight:400}}>{t('landing.perMonth')}</span></p>
              <p style={{textAlign:'center',fontSize:13,color:'var(--text-secondary)',marginBottom:16}}>{t('landing.monthlyBilled')}</p>
              <button onClick={()=>navigate('signup')} className="btn-secondary">{t('landing.startFreeTrial')}</button>
              <button onClick={()=>navigate('signup',{intent:'subscribe'})} style={{background:'none',border:'none',color:'var(--teal)',fontWeight:600,fontSize:13,marginTop:10,width:'100%',cursor:'pointer'}}>{t('landing.subscribeMonthly')}</button>
            </div>
          </div>
          <p style={{textAlign:'center',fontSize:13,color:'var(--text-secondary)',marginTop:16}}>{t('landing.trialNoRenew')}</p>
          <p style={{textAlign:'center',fontSize:13,color:'var(--text-secondary)',marginTop:6}}>{t('landing.schoolPlan')} <span onClick={()=>navigate('schools')} style={{color:'var(--teal)',fontWeight:600,cursor:'pointer',textDecoration:'underline'}}>{t('common.getSchoolLicense')}</span></p>
        </div>
      </div>

      {/* FAQ */}
      <div style={{padding:'48px 0'}}>
        <div className="mkt-inner mkt-faq">
          <h2 style={{textAlign:'center',marginBottom:24,fontSize:26}}>{t('landing.faqTitle')}</h2>
          {faqs.map((f,i)=>(
            <div key={i} className="faq-item" onClick={()=>setOpenFaq(openFaq===i?null:i)}>
              <div className="faq-question"><span>{f.q}</span><svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" width="18" height="18" style={{transform:openFaq===i?'rotate(180deg)':'none',transition:'0.2s'}}><polyline points="6,9 12,15 18,9"/></svg></div>
              {openFaq===i&&<p className="faq-answer">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Closing CTA */}
      <div style={{background:'var(--teal-dark)',color:'white',padding:'56px 24px'}}>
        <div style={{maxWidth:560,margin:'0 auto',textAlign:'center'}}>
          <h2 style={{color:'white',marginBottom:12,fontSize:26}}>{t('landing.closingTitle')}</h2>
          <p style={{color:'rgba(255,255,255,0.7)',marginBottom:24,fontSize:15}}>{t('landing.closingSubtitle')}</p>
          <div style={{maxWidth:400,margin:'0 auto'}}>
            <button onClick={()=>navigate('signup')} style={{background:'var(--amber)',color:'white',padding:'16px 32px',borderRadius:12,fontSize:16,fontWeight:700,width:'100%',border:'none',cursor:'pointer'}}>{t('landing.closingCta')}</button>
          </div>
          <p style={{marginTop:16,fontSize:13,color:'rgba(255,255,255,0.5)'}}>{t('landing.closingFootnote')}</p>
          <button onClick={()=>navigate('entrance')} style={{marginTop:20,background:'none',border:'none',color:'rgba(255,255,255,0.7)',fontSize:13,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6}}>
            <Icon name="play" size={14}/> {t('landing.replayEntrance')}
          </button>
        </div>
      </div>
    </div>
  );
}
